import { useState, useCallback } from 'react';
import { Volume2 } from 'lucide-react';
import {
  readSoundSettings,
  writeSoundSettings,
  type SoundSettings,
} from '../../lib/audio/soundSettings';
import { playFillTone, playKaChingTone, playWhaleTone, playOrderRejectedTone } from '../../lib/audio/tradeAudio';
import { playBootTone } from '../../lib/audio/defenseAudio';

// ── Sound toggle definitions ──────────────────────────────────────

interface SoundToggle {
  key: keyof SoundSettings;
  label: string;
  description: string;
  test: () => void;
}

const SOUND_TOGGLES: SoundToggle[] = [
  {
    key: 'tradeSoundEnabled',
    label: 'Trade Fills',
    description: 'Standard fill notification',
    test: playFillTone,
  },
  {
    key: 'firstSaleSoundEnabled',
    label: 'First Fill Ka-Ching',
    description: 'Cash register on first fill of an order',
    test: playKaChingTone,
  },
  {
    key: 'whaleSoundEnabled',
    label: 'Whale Alert',
    description: 'Deep tone on fills >= 1000 contracts',
    test: playWhaleTone,
  },
  {
    key: 'orderRejectionSoundEnabled',
    label: 'Order Rejected',
    description: 'Descending tone when exchange rejects order',
    test: playOrderRejectedTone,
  },
  {
    key: 'bootSoundEnabled',
    label: 'Boot Sound',
    description: 'Startup chime on app load',
    test: playBootTone,
  },
];

// ── Component ─────────────────────────────────────────────────────

export function SoundSettingsPanel() {
  const [settings, setSettings] = useState<SoundSettings>(readSoundSettings);

  const update = useCallback((patch: Partial<SoundSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      writeSoundSettings(next);
      return next;
    });
  }, []);

  return (
    <div className="bg-card border-border rounded-lg border p-5">
      {/* Header */}
      <div className="mb-5 flex items-center gap-2">
        <Volume2 size={18} className="text-muted-foreground" />
        <h2 className="text-base font-medium">Sound Notifications</h2>
      </div>

      {/* Volume slider */}
      <div className="mb-5">
        <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
          Volume — {Math.round(settings.volume * 100)}%
        </label>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(settings.volume * 100)}
          onChange={(e) => update({ volume: Number(e.target.value) / 100 })}
          className="bg-secondary h-1.5 w-full cursor-pointer appearance-none rounded-full accent-blue-500"
        />
      </div>

      {/* Sound toggles */}
      <div className="space-y-2.5">
        {SOUND_TOGGLES.map((toggle) => (
          <div
            key={toggle.key}
            className="bg-muted/50 flex items-center justify-between rounded-md px-3 py-2"
          >
            <div className="flex items-center gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={settings[toggle.key] as boolean}
                onClick={() => update({ [toggle.key]: !settings[toggle.key] })}
                className={`relative h-5 w-9 rounded-full transition-colors ${
                  settings[toggle.key] ? 'bg-blue-600' : 'bg-muted-foreground/30'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
                    settings[toggle.key] ? 'translate-x-4' : ''
                  }`}
                />
              </button>
              <div>
                <span className="text-foreground text-xs font-medium">{toggle.label}</span>
                <p className="text-muted-foreground text-xs">{toggle.description}</p>
              </div>
            </div>
            <button
              onClick={toggle.test}
              className="rounded-md px-2.5 py-1 text-xs text-blue-500 transition-colors hover:bg-blue-500/10 dark:text-blue-400"
            >
              Test
            </button>
          </div>
        ))}
      </div>

      {/* Min fill contracts */}
      <div className="mt-4">
        <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
          Min Fill Contracts (0 = all fills)
        </label>
        <input
          type="number"
          min={0}
          value={settings.minFillContracts}
          onChange={(e) => update({ minFillContracts: Math.max(0, Math.floor(Number(e.target.value) || 0)) })}
          className="bg-card border-border text-foreground w-24 rounded-md border px-3 py-1.5 text-xs focus:border-blue-500 focus:outline-none"
        />
      </div>
    </div>
  );
}
